import { ImmutableMap } from "@suse-o11y/immutable-map";
import { Template } from "@suse-o11y/template";
import { useSelector } from "react-redux";

import { Type, type Version, type ViewSnapshot } from "~/models/dto";

import { selectTimestampDate } from "../../../../../app/state/view/timeline/TimelineSelectors";
import { viewToSimpleStatefulComponent } from "../../../../../components/react/states/state-component-utils";
import { queryTopologySnapshot } from "../../../../../queries";
import { type UseQueryResult, useQuery } from "../../../../../queries/react-query";

const STQL_TEMPLATE = Template(
	`withNeighborsOf(
    components = (
      withNeighborsOf(
        components = (id = "\${POD_ID}" and type = "pod"),
        levels = "1",
        direction = "up"
      ) and type in ("deployment", "statefulset", "daemonset", "replicaset", "job", "pod")
    ),
    levels = "1",
    direction = "up"
  ) and type in ("deployment", "replicaset", "statefulset", "daemonset", "job", "cronjob")`.replace(/\s+/gu, " "),
);

const selectOwnerComponent = ({ components, relations, metadata: { componentTypes } }: ViewSnapshot) => {
	/**
	 * For the case of a Deployment and ReplicaSet, this will often lead to 2 components being returned,
	 * both the ReplicaSet and the Deployment object.
	 * In the case that there are 2 components, there will always be a relation in the results.
	 * To choose which component to show the diff of, choose the one that is on the outgoing side of the relation.
	 */

	const componentTypesMap = ImmutableMap.fromList(({ identifier }) => identifier!, componentTypes);

	const component =
		(components.length === 1 ? components[0] : undefined) ??
		(relations.length === 1 ? components.find(({ id }) => id === relations.at(0)?.source) : undefined);

	return component ? viewToSimpleStatefulComponent(component, componentTypesMap) : null;
};

export const useQueryPodOwnerComponent = ((ownedComponentId) => {
	const timestamp = useSelector(selectTimestampDate)?.valueOf();

	return useQuery({
		select: selectOwnerComponent,

		...queryTopologySnapshot({
			_type: Type.ViewSnapshotRequest,
			query: STQL_TEMPLATE.interpolate({ POD_ID: ownedComponentId }),
			queryVersion: "0.0.1" as Version,
			metadata: {
				_type: Type.QueryMetadata,
				groupingEnabled: false,
				showIndirectRelations: false,
				minGroupSize: 0,
				groupedByLayer: false,
				groupedByDomain: false,
				groupedByRelation: false,
				queryTime: timestamp,
				autoGrouping: false,
				connectedComponents: false,
				neighboringComponents: false,
				showFullComponent: true,
			},
		}),
	});
}) satisfies (ownedComponentId: number) => UseQueryResult;
